<template>
    <div v-if="previewData.length">
        <div v-if="errorRowsCount" class="mb-3 flex items-center">
            <b-checkbox v-model="showOnlyErrorRows">
                {{ $t('show_only_error_rows') }}
                <b-tag type="is-danger" rounded class="ml-1">{{ errorRowsCount }}</b-tag>
            </b-checkbox>
        </div>
        <div class="table-container">
            <b-table
            :key="showOnlyErrorRows ? 'errors' : 'all'"
            :data="tableRows"
            :loading="isLoading"
            class="text-xs import_xslx_table"
            :sticky-header="true"
            scrollable
            striped
            paginated
            :per-page="100"
            :pagination-simple="tableRows.length > 5000"
            :pagination-size="'is-small'"
            >
                <b-table-column v-for="(header, index) in headers" :key="index" :field="header"
                    :label="$t(header)" header-class="align-middle bg-blue header_xlsx">
                    <template #default="props">
                        <template v-if="header === 'import_status'">
                            <span class="text-center">
                                <b-tooltip
                                v-if="props.row.import_status == 'error'"
                                :label="generateTooltipLabel(props.row.status_error)"
                                position="is-right"
                                size="is-large"
                                multilined
                                type="is-danger">
                                    <b-tag type="is-danger">{{$t(props.row.import_status)}}</b-tag>
                                </b-tooltip>
                                <b-tag v-else :type="statusIconTypes[props.row.import_status]">{{$t(props.row.import_status)}}</b-tag>
                            </span>
                        </template>
                        <template v-else-if="header === 'actions' && props.row.status_error.length">
                            <span @click="openModal(props.row)" style="cursor: pointer;">
                                <b-icon pack="fa" size="is-small" icon="edit" class="ml-2 mt-2"></b-icon>
                            </span>
                        </template>
                        <template v-else-if="header === 'skip'">
                            <div class="ml-2 mt-2">
                                <b-field>
                                    <b-checkbox v-model="props.row[header]" @input="() => $emit('row-changed')"></b-checkbox>
                                </b-field>
                            </div>
                        </template>
                        <template v-else>
                            {{ props.row[header] }}
                        </template>
                    </template>
                </b-table-column>
                <import-excel-modal ref="dataModalExcel" @saved="handleSavedChanges"></import-excel-modal>
            </b-table>
        </div>
    </div>
</template>

<script>
import * as XLSX from 'xlsx';
import ImportExcelModal from "./import-excel-modal.vue";
import { decodeP6XerBytes, sanitizeP6ImportText } from '·/utils/p6XerText'; // sanitize texts

export default {
    components: {
        ImportExcelModal,
    },
    props: {
        file: {
            type: File,
            default: null,
        },
        projectId: {
            type: Number,
            required: true,
        },
        overwrite: {
            type: Boolean,
            default: false
        },
        entityType: {
            type: String,
            required: true
        },
        type: {
            type: String,
            // required: true
            default: null,
        },
        p6CodePrefix: {
            type: String,
            default: '',
        }
    },
    data() {
        return {
            previewData: [],
            isLoading: false,
            errors: [],
            selectedRowIndex: null,
            headers: [],
            showOnlyErrorRows: false,
            statusIconTypes: {
                start: 'is-warning',
                validated: 'is-primary',
                error: 'is-danger',
                progress: 'is-light',
                imported: 'is-success'
            },
            dateFields: [],
            p6Groups: [],  // Array de {code, name, parent_code} para la creación de grupos P6
            p6Calendar: null, // {work_days, holidays} extraídos del calendario P6
        };
    },

    computed: {
        errorRows() {
            return (this.previewData || []).filter((row) => row && row.import_status === 'error')
        },
        errorRowsCount() {
            return this.errorRows.length
        },
        tableRows() {
            if (this.showOnlyErrorRows && this.errorRowsCount) {
                return this.errorRows
            }
            return this.previewData
        },
    },

    methods: {
        generateTooltipLabel(errors) {
            return errors.map(error => this.$t(error.error, {data: error.data, '0': `${this.$t(error.field)} (${error.data})`})).join(', ');
        },

        async processFile() {
            if (!this.file) {
                return { success: false, message: 'No file selected' };
            }
            this.showOnlyErrorRows = false;

            // Si es Primavera P6 (XER), usamos lectura de texto plano
            if (this.type === 'primavera_p6') {
                return this.processP6Xer();
            }

            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                this.isLoading = true;

                reader.onload = async (event) => {
                    const data = event.target.result;
                    try {
                        const workbook = XLSX.read(data, { type: 'binary' });

                        // Recursos descompuestos y simple plano: leemos todas las hojas y detectamos cabeceras de grupo.
                        // Las hojas se mapean a capitulos nivel 2 y las filas-cabecera (col A con texto y col C vacia)
                        // a capitulos nivel 3 cuando se eligio un chapter raiz tipo tag al importar.
                        // Para el resto de tipos, mantenemos el flujo original de una sola hoja.
                        const isTagChapterImport = this.entityType === 'assignment'
                            && (this.type === 'resources_breakdown' || this.type === 'resources_bcs');

                        let filteredData;
                        if (isTagChapterImport) {
                            const sheetNameIndex = this.type === 'resources_breakdown' ? 16 : 10;
                            const groupNameIndex = this.type === 'resources_breakdown' ? 17 : 11;
                            filteredData = this.parseResourcesBreakdownWorkbook(workbook, sheetNameIndex, groupNameIndex);
                        } else {
                            const sheetName = workbook.SheetNames[0];
                            if (this.entityType === 'takeoff') {
                                this.normalizePercentFormattedCells(workbook.Sheets[sheetName], ['activity_weight', 'progress_percent']);
                            }
                            const worksheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: '' });
                            const rows = worksheet.slice(1);
                            filteredData = rows.filter(row => {
                                return row.some(value => value !== '' && value !== null && value !== undefined);
                            });
                        }

                        let fields = this.getFields()
                        let mappedData = filteredData.map((row) => {
                            let item = {
                                skip: false,
                                import_status: 'start',
                                status_error: '',
                                actions: ''
                            }

                            Object.entries(fields).map(field => {
                                if (this.dateFields.includes(field[0]) && typeof row[field[1]] === 'number' && this.isExcelDate(row[field[1]])) {
                                    const dateFmt = this.entityType === 'warehouse_packing_list' ? 'yyyy-mm-dd' : 'd/m/yyyy'
                                    row[field[1]] = XLSX.SSF.format(dateFmt, row[field[1]])
                                }
                                // Evitar que celdas vacías o columnas inexistentes (row[i] === undefined)
                                const value = row[field[1]]
                                item[field[0]] = (value === undefined || value === null) ? '' : `${value}`
                            })

                            return item
                        });

                        // Forward-fill del catálogo para BOQ eléctricas: el Excel
                        // viene agrupado — cada BOQ trae una fila de catálogo
                        // (code col0, line_pedido col1, description, unit) + su 1ª
                        // zona; las filas siguientes con esas columnas VACÍAS son
                        // zonas adicionales de la MISMA BOQ (solo cambian zona +
                        // cant/precio). El DIFERENCIADOR de BOQ es `line_pedido`
                        // (col 1): el `code` (col 0) se repite entre BOQs. Por eso
                        // detectamos "inicio de BOQ" por line_pedido y rellenamos
                        // el catálogo heredando la fila previa, para que cada fila
                        // quede autocontenida y el import sea chunk-safe (upsert
                        // por (code, line_pedido) + pivot por zona), sin estado
                        // entre chunks.
                        if (this.entityType === 'boq_detail' && this.type === 'electrical') {
                            const carryFields = ['code', 'line_pedido', 'description', 'unit'];
                            const last = {};
                            const isEmpty = (v) => v === undefined || v === null || `${v}`.trim() === '';
                            mappedData.forEach((item) => {
                                // Fila con line_pedido = inicio de BOQ: actualiza el
                                // arrastre con lo presente (el code puede venir solo
                                // en la 1ª BOQ del grupo).
                                if (!isEmpty(item.line_pedido)) {
                                    carryFields.forEach((f) => { if (!isEmpty(item[f])) last[f] = item[f]; });
                                }
                                // Completa columnas de catálogo vacías desde el
                                // arrastre (aplica a filas de zona y a filas de
                                // catálogo con code/desc/unit omitidos).
                                carryFields.forEach((f) => {
                                    if (isEmpty(item[f]) && last[f] !== undefined) item[f] = last[f];
                                });
                            });
                        }

                        this.previewData = mappedData;
                        this.getHeaders()
                        resolve({ success: true, message: 'File processed successfully', data: this.previewData });
                    } catch (error) {
                        console.error('Error reading XLSX data:', error);
                        reject({ success: false, message: 'Error reading XLSX data' });
                    } finally {
                        this.isLoading = false;
                    }
                };

                reader.onerror = (error) => {
                    this.isLoading = false;
                    console.error('Error reading file:', error);
                    reject({ success: false, message: 'Error reading file' });
                };

                reader.readAsBinaryString(this.file);
            });
        },

        // Celda Excel con formato % llega como fracción (600% → 6, 60% → 0.6): sin el formato el
        // backend no distingue 6 (= 600%) de 6%. Se reescribe como texto explícito "600%".
        normalizePercentFormattedCells(sheet, fieldNames) {
            if (!sheet || !sheet['!ref']) {
                return;
            }
            const fields = this.getFields() || {};
            const columns = fieldNames.map(name => fields[name]).filter(col => col !== undefined);
            const range = XLSX.utils.decode_range(sheet['!ref']);

            for (let r = range.s.r + 1; r <= range.e.r; r++) {
                columns.forEach((c) => {
                    const cell = sheet[XLSX.utils.encode_cell({ r, c })];
                    if (!cell || cell.t !== 'n' || !/%/.test(cell.z || cell.w || '')) {
                        return;
                    }
                    cell.v = `${parseFloat((cell.v * 100).toFixed(5))}%`;
                    cell.t = 's';
                });
            }
        },

        openModal(row) {
            this.selectedRowIndex = this.previewData.indexOf(row);
            this.$refs.dataModalExcel.show(row);
        },
        // Método para manejar los cambios guardados recibidos del modal
        handleSavedChanges(updatedRow) {
            if (this.selectedRowIndex !== null) {
                updatedRow.import_status = 'start'
                this.$set(this.previewData, this.selectedRowIndex, updatedRow);
                this.selectedRowIndex = null;
                this.$emit('row-changed', updatedRow);
            }
        },

        updateDataErrors(errors) {
            for (let i in errors) {
                const idx = parseInt(i, 10);
                const row = this.previewData[idx];
                if (!row) {
                    continue;
                }
                row.import_status = 'error';
                row.status_error = errors[`${i}`];
            }
        },

        /**
         * lastIndex is the global row index (0-based). Backend used to overstated it when the last chunk
         * had fewer than chunk rows; clamp to preview length to avoid undefined access when restoring UI state.
         */
        updateDataStatus(lastIndex, status) {
            if (!Array.isArray(this.previewData) || !this.previewData.length || lastIndex == null || lastIndex < 0) {
                return;
            }
            const max = Math.min(lastIndex, this.previewData.length - 1);
            for (let i = 0; i <= max; i++) {
                const row = this.previewData[i];
                if (!row) {
                    continue;
                }
                if (row.import_status != 'imported' && !row.skip) {
                    row.import_status = status;
                }
            }
        },

        // Valida si un número es una fecha de Excel
        isExcelDate(value) {
            // Excel usa el 1 de enero de 1900 como base para las fechas
            const excelEpoch = new Date(1900, 0, 1);
            const daysFromEpoch = value - 1; // Ajusta el día base
            const excelDate = new Date(excelEpoch.getTime() + daysFromEpoch * 86400000);
            return !isNaN(excelDate.getTime());
        },

        getHeaders() {
            if (!this.previewData.length) {
                return;
            }
            this.headers = (Object.keys(this.previewData[0])).filter(item => item != 'status_error')
        },

        setData(data) {
            this.isLoading = true
            this.showOnlyErrorRows = false
            this.previewData = data
            this.getHeaders()
            this.isLoading = false
        },

        // Leemos el archivo XER y generamos actividades (prelations) y grupos (p6Groups)
        processP6Xer() {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                this.isLoading = true;

                reader.onload = (event) => {
                    try {
                        const content = decodeP6XerBytes(event.target.result);
                        const lines = content.split('\n');

                        const relTypeMap = { 'PR_FS': 'FC', 'PR_FF': 'FF', 'PR_SS': 'CC', 'PR_SF': 'CF' };
                        const statusMap = { 'TK_Complete': '4', 'TK_Active': '1', 'TK_NotStart': '0' };

                        // Parseamos las secciones que necesitamos del XER
                        const allWbsRows = this.parseXerSection(lines, 'PROJWBS');
                        const allTaskRows = this.parseXerSection(lines, 'TASK');
                        const allPredRows = this.parseXerSection(lines, 'TASKPRED');
                        const allRsrcRows = this.parseXerSection(lines, 'RSRC');
                        const allTaskRsrcRows = this.parseXerSection(lines, 'TASKRSRC');

                        // Si el XER tiene varios proyectos, detectamos cuál queremos importar
                        const projId = this.detectP6Project(allWbsRows, allTaskRows);

                        // Extraemos el calendario del proyecto P6
                        this.p6Calendar = this.extractP6Calendar(lines, projId);

                        // Filtramos solo los datos del proyecto seleccionado
                        const wbsRows = allWbsRows.filter(w => w.proj_id === projId);
                        const taskRows = allTaskRows.filter(t => t.proj_id === projId);
                        const predRows = allPredRows.filter(p => p.proj_id === projId);
                        const taskRsrcRows = allTaskRsrcRows.filter(tr => tr.proj_id === projId);

                        // --- RSRC: mapa rsrc_id → { code, type } ---
                        const rsrcMap = {};
                        allRsrcRows.forEach(r => {
                            rsrcMap[r.rsrc_id] = { code: r.rsrc_short_name, type: r.rsrc_type };
                        });

                        // --- TASKRSRC: agrupamos recursos por task_id ---
                        const toNum = (s) => s ? parseFloat(String(s).replace(',', '.')) || 0 : 0;
                        const taskResourcesMap = {};
                        taskRsrcRows.forEach(tr => {
                            const rsrc = rsrcMap[tr.rsrc_id];
                            if (!rsrc) return;
                            if (!taskResourcesMap[tr.task_id]) taskResourcesMap[tr.task_id] = { hh: 0, materials: [] };
                            const budgetedQty = toNum(tr.target_qty);
                            if (rsrc.type === 'RT_Labor') {
                                taskResourcesMap[tr.task_id].hh += budgetedQty;
                            } else if (rsrc.type === 'RT_Mat') {
                                taskResourcesMap[tr.task_id].materials.push(`${rsrc.code}:${budgetedQty}`);
                            }
                        });

                        // --- WBS: armamos la jerarquía y los códigos completos ---
                        const wbsMap = {};
                        wbsRows.forEach(w => wbsMap[w.wbs_id] = w);

                        // Omitimos siempre el nodo raíz del WBS (proj_node_flag=Y)
                        const rootWbsId = wbsRows.find(w => w.proj_node_flag === 'Y')?.wbs_id;

                        // El código completo incluye el nombre del nodo raíz (p. ej. "UTE 26-04.ABC").
                        // La raíz no se crea como grupo, pero su texto forma parte del código de los hijos
                        // para que el prefijo pueda recortarlo sobre el código completo.
                        const buildFullCode = (wbsId) => {
                            const parts = [];
                            let current = wbsId;
                            while (current && wbsMap[current]) {
                                parts.unshift(wbsMap[current].wbs_short_name);
                                current = wbsMap[current].parent_wbs_id;
                            }
                            return parts.join('.');
                        };

                        // Prefijo a remover de los códigos WBS: quitamos exactamente el texto escrito,
                        // solo la primera aparición y esté donde esté (no solo al inicio)
                        const wbsPrefix = (this.p6CodePrefix || '').trim();

                        const stripPrefix = (code) => {
                            if (!wbsPrefix) return code;
                            const idx = code.indexOf(wbsPrefix);
                            return idx === -1 ? code : code.slice(0, idx) + code.slice(idx + wbsPrefix.length);
                        };

                        // Precalculamos el código completo de cada WBS (con raíz incluida) ya recortado por el prefijo
                        const wbsFullCodes = {};
                        Object.keys(wbsMap).forEach(id => {
                            wbsFullCodes[id] = stripPrefix(buildFullCode(id));
                        });

                        // Generamos el array de grupos, omitiendo el nodo raíz y los que queden sin código tras el prefijo
                        this.p6Groups = Object.entries(wbsMap)
                        .filter(([id]) => id !== rootWbsId && wbsFullCodes[id])
                        .map(([id, w]) => {
                            const code = wbsFullCodes[id];
                            // Si el padre es el nodo raíz (que no se crea como grupo), este grupo es de primer nivel.
                            // Si no, tomamos el código completo del padre; vacío => se trata como raíz.
                            const parentIsRoot = w.parent_wbs_id === rootWbsId;
                            const parentCode = (!parentIsRoot && w.parent_wbs_id && wbsMap[w.parent_wbs_id])
                                ? wbsFullCodes[w.parent_wbs_id]
                                : null;
                            const displayName = sanitizeP6ImportText(w.wbs_name || '') || code;
                            return { code, name: displayName, parent_code: parentCode || null };
                        });

                        // Códigos de grupo que realmente se crean. Los nodos omitidos (raíz o
                        // vaciados por el prefijo) no están aquí, así que las actividades que
                        // cuelgan de ellos no deben referenciar un grupo inexistente: van sin grupo.
                        const createdGroupCodes = new Set(this.p6Groups.map(g => g.code));

                        // --- TASK: filtramos solo actividades reales (TT_Task, milestones y LOE) ---
                        const activityRows = taskRows.filter(t =>
                            t.task_type === 'TT_Task' || t.task_type === 'TT_FinMile' || t.task_type === 'TT_Mile' || t.task_type === 'TT_LOE'
                        );

                        // Asignamos id_column secuencial y mapeamos task_id -> id_column
                        const taskIdToIdColumn = {};
                        activityRows.forEach((t, idx) => {
                            taskIdToIdColumn[t.task_id] = idx + 1;
                        });

                        // --- TASKPRED: armamos predecesoras y sucesoras ---
                        const predecessorsMap = {};
                        const successorsMap = {};

                        predRows.forEach(rel => {
                            const predIdCol = taskIdToIdColumn[rel.pred_task_id];
                            const succIdCol = taskIdToIdColumn[rel.task_id];
                            const relType = relTypeMap[rel.pred_type] || 'FC';
                            const lagHours = parseFloat(rel.lag_hr_cnt) || 0;
                            const lagDays = lagHours !== 0 ? Math.round(lagHours / 8) : 0;
                            const lagStr = lagDays !== 0 ? `+${lagDays}d` : '';

                            if (predIdCol && succIdCol) {
                                // Para el sucesor: registramos su predecesor
                                if (!predecessorsMap[rel.task_id]) predecessorsMap[rel.task_id] = [];
                                predecessorsMap[rel.task_id].push(`${predIdCol}${relType}${lagStr}`);

                                // Para el predecesor: registramos su sucesor
                                if (!successorsMap[rel.pred_task_id]) successorsMap[rel.pred_task_id] = [];
                                successorsMap[rel.pred_task_id].push(`${succIdCol}${relType}${lagStr}`);
                            }
                        });

                        // --- Generamos las filas en formato prelations ---
                        const formatDate = (dateStr) => {
                            if (!dateStr) return '';
                            // El XER trae fechas como "2025-08-08 09:00", convertimos a d/m/yyyy
                            const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
                            if (!match) return dateStr;
                            return `${parseInt(match[3])}/${parseInt(match[2])}/${match[1]}`;
                        };

                        // Calcula el Activity % Complete según el tipo de cálculo de P6
                        const calcActivityPct = (task) => {
                            const toNum = (s) => s ? parseFloat(String(s).replace(',', '.')) || 0 : 0;
                            const remainWork = toNum(task.remain_work_qty);
                            const targetWork = toNum(task.target_work_qty);
                            const remainDrtn = toNum(task.remain_drtn_hr_cnt);
                            const targetDrtn = toNum(task.target_drtn_hr_cnt);

                            let pct = 0;
                            if (task.complete_pct_type === 'CP_Drtn') {
                                pct = targetDrtn > 0 ? Math.round((1 - remainDrtn / targetDrtn) * 10000) / 100 : 0;
                            } else if (targetWork > 0) {
                                // CP_Units: usa work quantities, fallback a duración si no hay resources
                                pct = Math.round((1 - remainWork / targetWork) * 10000) / 100;
                            } else {
                                pct = targetDrtn > 0 ? Math.round((1 - remainDrtn / targetDrtn) * 10000) / 100 : 0;
                            }
                            return Math.min(100, Math.max(0, pct));
                        };

                        this.previewData = activityRows.map((task, idx) => {
                            const rawWbsCode = sanitizeP6ImportText(wbsFullCodes[task.wbs_id] || '');
                            // Si el grupo no se creó (nodo raíz o vaciado por el prefijo), la actividad va sin grupo
                            const wbsCode = createdGroupCodes.has(rawWbsCode) ? rawWbsCode : '';
                            const status = statusMap[task.status_code] || '0';
                            // Separamos Activity % Complete y Physical % Complete de P6
                            const p6ActivityProgress = String(calcActivityPct(task));
                            const toNum = (s) => s ? parseFloat(String(s).replace(',', '.')) || 0 : 0;
                            const p6PhysicalProgress = String(toNum(task.phys_complete_pct));
                            const isComplete = task.status_code === 'TK_Complete';
                            const isActive = task.status_code === 'TK_Active';

                            // Current Start: Complete/Active → act_start, NotStart → restart o early_start
                            const currentStart = (isComplete || isActive)
                                ? (task.act_start_date || task.restart_date || task.early_start_date)
                                : (task.restart_date || task.early_start_date);
                            // Current End: Complete → act_end, Active/NotStart → reend o early_end
                            const currentEnd = isComplete
                                ? (task.act_end_date || task.reend_date || task.early_end_date)
                                : (task.reend_date || task.early_end_date);

                            return {
                                skip: false,
                                import_status: 'start',
                                status_error: '',
                                actions: '',
                                group_code: wbsCode,
                                id_column: `${idx + 1}`,
                                planning_code: sanitizeP6ImportText(String(task.task_code || '')),
                                new_planning_code: '',
                                parent_activity_planning_code: '',
                                name: sanitizeP6ImportText(task.task_name || ''),
                                in_planning: '1',
                                critical_path: task.total_float_hr_cnt === '0' ? '1' : '',
                                scheduled_start_date: formatDate(task.target_start_date),
                                scheduled_end_date: formatDate(task.target_end_date),
                                current_start_date: formatDate(currentStart),
                                current_end_date: formatDate(currentEnd),
                                predecessors: (predecessorsMap[task.task_id] || []).join(';'),
                                successors: (successorsMap[task.task_id] || []).join(';'),
                                real_start_date: formatDate(task.act_start_date),
                                real_end_date: formatDate(task.act_end_date),
                                progress: '',
                                weight: '',
                                email_manager: '',
                                location_reference_code: '',
                                participants: '',
                                collaborators: '',
                                tags: '',
                                status,
                                show_on_devices: '',
                                description: '',
                                document_codes: '',
                                flow_codes: '',
                                p6_budgeted_mh: taskResourcesMap[task.task_id]?.hh || '',
                                p6_material_resources: taskResourcesMap[task.task_id]?.materials?.join(';') || '',
                                p6_activity_progress: p6ActivityProgress,
                                p6_physical_progress: p6PhysicalProgress
                            };
                        });

                        this.getHeaders();
                        resolve({ success: true, message: 'File processed successfully', data: this.previewData });
                    } catch (error) {
                        console.error('Error reading XER data:', error);
                        reject({ success: false, message: 'Error reading XER data' });
                    } finally {
                        this.isLoading = false;
                    }
                };

                reader.onerror = (error) => {
                    this.isLoading = false;
                    console.error('Error reading file:', error);
                    reject({ success: false, message: 'Error reading file' });
                };

                reader.readAsArrayBuffer(this.file);
            });
        },

        // Devuelve un array de filas (arrays) de TODAS las hojas del workbook, anotando cada fila con:
        //   - sheet_name (nombre de la pestaña Excel = capitulo nivel 2)
        //   - group_name (ultima fila-cabecera vista en la hoja = capitulo nivel 3)
        // Las filas-cabecera (col A con texto y col C vacia) NO se devuelven como datos, solo actualizan el grupo actual.
        // sheetNameIndex/groupNameIndex: breakdown 16/17, simple plano 10/11 (tras las columnas reales del Excel).
        parseResourcesBreakdownWorkbook(workbook, sheetNameIndex = 16, groupNameIndex = 17) {
            const result = [];
            const isEmpty = (v) => v === '' || v === null || v === undefined;

            for (const sheetName of workbook.SheetNames) {
                const worksheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: '' });
                // saltamos cabecera columnas (fila 0) y arrancamos sin grupo
                let currentGroup = '';
                for (let r = 1; r < worksheet.length; r++) {
                    const row = worksheet[r] || [];
                    if (row.every(isEmpty)) continue;

                    const colA = row[0];
                    const colC = row[2]; // breakdown: variant_type; simple: measurement_code. Cabecera de grupo la deja vacia.
                    const hasA = !isEmpty(colA);
                    const hasC = !isEmpty(colC);

                    // Fila-cabecera: solo nombre de grupo, sin datos de medicion
                    if (hasA && !hasC) {
                        currentGroup = String(colA).trim();
                        continue;
                    }

                    // Fila vacia o invalida la saltamos
                    if (!hasA) continue;

                    // Fila de datos: clonamos para no mutar lo que devolvio XLSX y anotamos hoja/grupo
                    const enriched = row.slice();
                    enriched[sheetNameIndex] = sheetName;
                    enriched[groupNameIndex] = currentGroup;
                    result.push(enriched);
                }
            }

            return result;
        },

        // Parsea una sección del XER y devuelve un array de objetos {campo: valor}
        parseXerSection(lines, sectionName) {
            let inSection = false;
            let fields = [];
            const rows = [];

            for (const line of lines) {
                if (line.trim() === `%T\t${sectionName}`) { inSection = true; continue; }
                if (inSection && line.startsWith('%F\t')) {
                    fields = line.split('\t').slice(1).map(f => f.trim());
                    continue;
                }
                if (inSection && line.startsWith('%T\t')) break;
                if (inSection && line.startsWith('%R\t')) {
                    const vals = line.split('\t').slice(1);
                    const obj = {};
                    fields.forEach((f, i) => obj[f] = (vals[i] || '').trim());
                    rows.push(obj);
                }
            }
            return rows;
        },
        // Si el XER tiene varios proyectos, elegimos el correcto:
        // 1. Intentamos match por nombre de archivo
        // 2. Si no, tomamos el que tiene más actividades
        detectP6Project(wbsRows, taskRows) {
            const roots = wbsRows.filter(w => w.proj_node_flag === 'Y');
            if (roots.length <= 1) return roots[0]?.proj_id || wbsRows[0]?.proj_id;

            // Match por nombre de archivo (comparación parcial, sin extensión)
            const fileName = (this.file?.name || '').replace(/\.[^.]+$/, '').toLowerCase();
            if (fileName) {
                const match = roots.find(r => {
                    const code = r.wbs_short_name.toLowerCase();
                    return fileName.includes(code) || code.includes(fileName);
                });
                if (match) return match.proj_id;
            }

            // Fallback: el proyecto con más actividades
            const taskCounts = {};
            taskRows.forEach(t => taskCounts[t.proj_id] = (taskCounts[t.proj_id] || 0) + 1);
            return roots.sort((a, b) => (taskCounts[b.proj_id] || 0) - (taskCounts[a.proj_id] || 0))[0].proj_id;
        },

        // Extraemos work_days y holidays del calendario P6 asociado al proyecto
        extractP6Calendar(lines, projId) {
            const projectRows = this.parseXerSection(lines, 'PROJECT');
            const projRow = projectRows.find(p => p.proj_id === projId);
            if (!projRow || !projRow.clndr_id) return null;

            const calendarRows = this.parseXerSection(lines, 'CALENDAR');
            const cal = calendarRows.find(c => c.clndr_id === projRow.clndr_id);
            if (!cal || !cal.clndr_data) return null;

            // P6 DaysOfWeek: 1=domingo, 2=lunes, ..., 7=sábado
            // Un día es laborable si su bloque contiene al menos un turno (s|HH:MM)
            const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const workDays = {};
            const data = cal.clndr_data;
            // Acotamos la búsqueda al bloque DaysOfWeek para no confundir con Exceptions
            const dowStart = data.indexOf('DaysOfWeek');
            const dowEnd = data.indexOf('VIEW(');
            const dowBlock = (dowStart !== -1 && dowEnd !== -1) ? data.substring(dowStart, dowEnd) : data;
            for (let d = 1; d <= 7; d++) {
                const start = dowBlock.indexOf(`(0||${d}(`);
                if (start === -1) { workDays[dayKeys[d - 1]] = false; continue; }
                const nextDay = dowBlock.indexOf(`(0||${d + 1}(`, start);
                const block = nextDay !== -1 ? dowBlock.substring(start, nextDay) : dowBlock.substring(start);
                workDays[dayKeys[d - 1]] = block.includes('s|');
            }

            // Holidays: serial P6 (serial - 1 dias desde 1899-12-30, por el bug de Lotus 1-2-3)
            const p6Epoch = new Date(1899, 11, 30); // 1899-12-30
            const holidays = [];
            const excBlock = data.substring(data.indexOf('Exceptions'));
            const holPattern = /\(d\|(\d+)\)/g;
            let hm;
            while ((hm = holPattern.exec(excBlock)) !== null) {
                const serial = parseInt(hm[1]);
                const date = new Date(p6Epoch.getTime() + serial * 86400000);
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                holidays.push(`${y}-${m}-${dd}`);
            }

            return { work_days: workDays, holidays };
        },

        getFields(type = this.type) {
            switch (this.entityType) {
                case 'activities':
                    this.dateFields = ['scheduled_start_date', 'scheduled_end_date', 'current_start_date', 'current_end_date', 'real_start_date', 'real_end_date']
                    switch (type) {
                        case 'simple':
                            return {
                                group_code: 0,
                                planning_code: 1,
                                new_planning_code: 2,
                                parent_activity_planning_code: 3,
                                name: 4,
                                in_planning: 5,
                                critical_path: 6,
                                scheduled_start_date: 7,
                                scheduled_end_date: 8,
                                current_start_date: 9,
                                current_end_date: 10,
                                real_start_date: 11,
                                real_end_date: 12,
                                progress: 13,
                                weight: 14,
                                email_manager: 15,
                                location_reference_code: 16,
                                participants: 17,
                                collaborators: 18,
                                tags: 19,
                                status: 20,
                                show_on_devices: 21,
                                description: 22,
                                document_codes: 23,
                                flow_codes: 24
                            }
                        case 'primavera_p6':
                        case 'prelations':
                            return {
                                group_code: 0,
                                id_column: 1,
                                planning_code: 2,
                                new_planning_code: 3,
                                parent_activity_planning_code: 4,
                                name: 5,
                                in_planning: 6,
                                critical_path: 7,
                                scheduled_start_date: 8,
                                scheduled_end_date: 9,
                                current_start_date: 10,
                                current_end_date: 11,
                                predecessors: 12,
                                successors: 13,
                                real_start_date: 14,
                                real_end_date: 15,
                                progress: 16,
                                weight: 17,
                                email_manager: 18,
                                location_reference_code: 19,
                                participants: 20,
                                collaborators: 21,
                                tags: 22,
                                status: 23,
                                show_on_devices: 24,
                                description: 25,
                                document_codes: 26,
                                flow_codes: 27,
                                p6_budgeted_mh: 28,
                                p6_material_resources: 29,
                                p6_activity_progress: 30,
                                p6_physical_progress: 31
                            }
                    }
                    break;
                case 'assignment':
                    switch(type) {
                        case 'resources_bcs':
                            return {
                                activity: 0,
                                activity_name: 1,
                                measurement_code: 2,
                                measurement_name: 3,
                                measurement_unit_id: 4,
                                total_quantity: 5,
                                weight: 6,
                                unit_price: 7,
                                code_order: 8,
                                position: 9,
                                // Columnas ficticias no presentes en el Excel, solo se usan en entorno EAG al importar bajo un chapter tag (capitulo tipo etiqueta) (nivel 1 elegido por el usuario)
                                sheet_name: 10,
                                group_name: 11,
                            }
                        // Simple CON BOQ: mismas columnas que `simple`, pero la col. 7
                        // (que en simple es unit_price) trae el `boq_line_pedido`: con él
                        // + la cabecera BOQ elegida se busca la boq_detail para
                        // validar cantidad y sacar el precio unitario al importar.
                        case 'resources_bcs_boq':
                            return {
                                activity: 0,
                                activity_name: 1,
                                measurement_code: 2,
                                measurement_name: 3,
                                measurement_unit_id: 4,
                                total_quantity: 5,
                                weight: 6,
                                boq_line_pedido: 7,
                                code_order: 8,
                                position: 9,
                            }
                        case 'resources_breakdown':
                            return {
                                activity: 0,
                                activity_name: 1,
                                variant_type: 2,
                                measurement_parent_code: 3,
                                measurement_parent_name: 4,
                                measurement_parent_unit_id: 5,
                                parent_total_quantity: 6,
                                parent_weight: 7,
                                measurement_code: 8,
                                measurement_name: 9,
                                measurement_unit_id: 10,
                                total_quantity: 11,
                                weight: 12,
                                unit_price: 13,
                                code_order: 14,
                                position: 15,
                                // Capitulos auto-generados al importar bajo un chapter tag (nivel 1 elegido por el usuario)
                                sheet_name: 16,
                                group_name: 17,
                            }
                        case 'task_subtask':
                            return {
                                activity: 0,
                                measurement_code: 1,
                                parent_code: 2,
                                type: 3,
                                name: 4,
                                measurement_unit_id: 5,
                                unit_price: 6,
                                total_quantity: 7,
                                weight: 8,
                                executed_quantity: 9,
                                completed: 10,
                                comentario: 11
                            }
                    }
                    break;
                case 'warehouse_packing_list':
                    this.dateFields = ['expedition_date', 'reception_date']
                    switch (type) {
                        case 'simple':
                            return {
                                work_number: 0,
                                expedition_number: 1,
                                expedition_description: 2,
                                expedition_date: 3,
                                reception_date: 4,
                                packing_list_number: 5,
                                container_number: 6,
                                entry_type: 7,
                                package_number: 8,
                                order_number: 9,
                                provider: 10,
                                package_type: 11,
                                package_description: 12,
                                material_description: 13,
                                unique_material_id: 14,
                                received_quantity: 15,
                                unit: 16,
                                category: 17,
                                drawing_number: 18,
                                tag_kks: 19,
                                length_cm: 20,
                                width_cm: 21,
                                height_cm: 22,
                                volume_m3: 23,
                                net_weight_kg: 24,
                                gross_weight_kg: 25,
                                stackable: 26,
                                warehouse_type: 27,
                                warehouse: 28,
                                location: 29,
                                status: 30,
                                comments: 31,
                            }
                    }
                    break;
                case 'boq_detail':
                    switch (type) {
                        case 'mechanical':
                        case 'civil':
                            // Civil usa el mismo layout que mecánico (sin zona).
                            return {
                                code:        0,
                                line_pedido: 1,
                                description: 2,
                                unit:        3,
                                quantity:    4,
                                price_unit:  5,
                                price_total: 6,
                            }
                        case 'electrical':
                            // Eléctrico / zanjas: mismas columnas + `zone` en la
                            // última (índice 7). Un mismo code puede repetirse en
                            // varias filas (una por zona); la cantidad/precio van
                            // INLINE en la fila por (boq, zona) (el pivot de zonas se eliminó).
                            return {
                                code:        0,
                                line_pedido: 1,
                                description: 2,
                                unit:        3,
                                quantity:    4,
                                price_unit:  5,
                                price_total: 6,
                                zone:        7,
                            }
                    }
                    break;
                
                case 'takeoff':
                    switch (type) {
                        case 'type1':
                            // Orden Excel A..AF (coincide con ImportTakeoffsOptimized::COLUMNS)
                            // W=stage (Etapa assignment.name); AF=niveis (BoqRoot stage)
                            // AD/AE = informativos (consumed_mh, pending_mh_epct)
                            return {
                                project_name: 0,
                                site: 1,
                                client: 2,
                                contractor: 3,
                                primary_key: 4,
                                discipline: 5,
                                cycle: 6,
                                area: 7,
                                schedule_id: 8,
                                tag: 9,
                                tag_type_or_material: 10,
                                tag_description: 11,
                                kks: 12,
                                engineering_doc: 13,
                                engineering_doc_rev: 14,
                                pids_drawing: 15,
                                supplier: 16,
                                correspondence: 17,
                                origin: 18,
                                destination: 19,
                                planner: 20,
                                activity: 21,
                                stage: 22,
                                total_activity_qty: 23,
                                activity_weight: 24,
                                measure: 25,
                                progress_percent: 26,
                                mh_index: 27,
                                scheduled_mh: 28,
                                consumed_mh: 29,
                                pending_mh_epct: 30,
                                niveis: 31,
                            }
                        case 'type2':
                            // Orden Excel A..AC (coincide con ImportTakeoffsOptimized::TYPE2_COLUMNS)
                            // R=stage (Etapa); AC=niveis (BoqRoot stage)
                            return {
                                project_name: 0,
                                site: 1,
                                client: 2,
                                contractor: 3,
                                primary_key: 4,
                                discipline: 5,
                                cycle: 6,
                                area: 7,
                                schedule_id: 8,
                                tag: 9,
                                tag_description: 10,
                                kks: 11,
                                engineering_doc: 12,
                                engineering_doc_rev: 13,
                                supplier: 14,
                                planner: 15,
                                activity: 16,
                                stage: 17,
                                total_activity_qty: 18,
                                activity_weight: 19,
                                measure: 20,
                                supervisor: 21,
                                progress_percent: 22,
                                activity_description: 23,
                                mh_index: 24,
                                scheduled_mh: 25,
                                consumed_mh: 26,
                                pending_mh_epct: 27,
                                niveis: 28,
                            }
                    }
                    break;
            }
        }

    },
};
</script>

<style>
.table-container {
    overflow-x: auto;
}

.header_xlsx {
    background: #5577BB !important;
    vertical-align: middle !important;
    text-align: center !important;
}
.import_xslx_table > .table-container {
    height: 500px !important;
}
</style>
